import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import MapView from './components/MapView.jsx'
import Panel from './components/Panel.jsx'
import { SearchField, SearchResults } from './components/Search.jsx'
import StayChips from './components/StayChips.jsx'
import { HomeBody } from './components/HomeView.jsx'
import { SectionHeader, SectionBody, sectionModel } from './components/SectionCard.jsx'
import { TopControls, BottomControls, StatusChips } from './components/MapUI.jsx'
import Legend from './components/Legend.jsx'
import LayersPopover from './components/LayersPopover.jsx'
import { LogoP } from './components/Icons.jsx'
import { useLayoutMode, useFinePointer, useMediaQuery } from './lib/useMediaQuery.js'
import { useLocalStorage } from './lib/useLocalStorage.js'
import { useTheme } from './lib/useTheme.js'
import { useGeocoder } from './lib/useGeocoder.js'
import { fetchZones } from './lib/api.js'
import { useClock } from './lib/useClock.js'
import { makeT } from './lib/i18n.js'
import { geometryCenter } from './lib/geo.js'
import { BASEMAP_IDS } from './lib/basemaps.js'
import { evaluateSection } from '../../shared/freeNow.js'
import { pragueParts } from '../../shared/time.js'
import { isCzechHoliday, czechHolidayName } from '../../shared/holidays.js'
import { parseDuration } from '../../shared/duration.js'
import { estimateCost } from '../../shared/cost.js'
import { buildHeatScale } from '../../shared/heat.js'

// First run follows the browser language; the stored choice wins after that.
const defaultLang =
  typeof navigator !== 'undefined' && (navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'cs'

// Every visitor's basemap used to be saved whether they picked it or not, so a
// new default never reached anyone. Choices live under a new key now (and are
// only stored once made); drop the old one so everyone starts on Detailed.
try {
  window.localStorage.removeItem('zpsp.basemap')
} catch {
  /* storage disabled */
}

const LOCATE_ERRORS = {
  denied: 'locateDenied',
  timeout: 'locateTimeout',
  unavailable: 'locateUnavailable',
  unsupported: 'locateUnsupported',
}

export default function App() {
  const [lang, setLang] = useLocalStorage('zpsp.lang', defaultLang, (v) => ['cs', 'en'].includes(v))
  const t = useMemo(() => makeT(lang), [lang])
  const mode = useLayoutMode() // 'sheet' | 'side'
  const finePointer = useFinePointer()
  const shortScreen = useMediaQuery('(max-height: 540px)') // phones held sideways
  const { theme, pref: themePref, setPref: setThemePref } = useTheme()

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // ---- data ------------------------------------------------------------
  const [zones, setZones] = useState(null)
  const [source, setSource] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapError, setMapError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetchZones()
      .then(({ data, source: src }) => {
        if (!alive) return
        setZones(data)
        setSource(src)
        setLoading(false)
      })
      .catch((err) => {
        if (!alive) return
        setError(err.message)
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [reloadKey])

  // ---- map display -----------------------------------------------------
  const [colorMode, setColorMode] = useState('status')
  const [basemap, setBasemap] = useLocalStorage('zpsp.basemap.v2', 'detailed', (v) => BASEMAP_IDS.includes(v))
  const [basemapShown, setBasemapShown] = useState(basemap) // differs after a fallback
  useEffect(() => setBasemapShown(basemap), [basemap, theme])
  const [layersOpen, setLayersOpen] = useState(false)
  const [bearing, setBearing] = useState(0)
  const mapApi = useRef(null)

  // ---- the plan: when + how long ---------------------------------------
  const [overrideAt, setOverrideAt] = useState(null) // epoch ms, or null = live now
  const [durationText, setDurationText] = useState('')
  const [whenOpen, setWhenOpen] = useState(false)

  const now = useClock(30_000)
  const isOverride = overrideAt != null
  const effectiveDate = useMemo(() => (isOverride ? new Date(overrideAt) : now), [isOverride, overrideAt, now])
  const setWhen = (d) => setOverrideAt(d ? d.getTime() : null)
  // Dragging the time slider would otherwise re-price 5 500+ sections per input
  // event; the expensive consumers read this deferred copy instead.
  const mapDate = useDeferredValue(effectiveDate)

  const parsedDuration = useMemo(() => parseDuration(durationText, effectiveDate), [durationText, effectiveDate])
  const durationMin = parsedDuration.ok ? parsedDuration.minutes : null
  // Same for the stay slider: the thumb and its label follow the finger, the
  // 5 500-section re-pricing catches up when it can.
  const mapDuration = useDeferredValue(durationMin)

  // ---- navigation: home · search · section -----------------------------
  const [selectedId, setSelectedId] = useState(null)
  const [searchActive, setSearchActive] = useState(false)
  const [query, setQuery] = useState('')
  const [searchPin, setSearchPin] = useState(null)
  const [flyTo, setFlyTo] = useState(null)
  const [detent, setDetent] = useState('peek')
  const homeDetent = useRef('peek')
  const [collapsed, setCollapsed] = useLocalStorage('zpsp.panelCollapsed', false, (v) => typeof v === 'boolean')
  const [occlusion, setOcclusion] = useState({ bottom: 0, left: 0 })
  const searchInput = useRef(null)
  const cardTitle = useRef(null)
  const geo = useGeocoder(searchActive ? query : '')
  const view = searchActive ? 'search' : selectedId ? 'section' : 'home'

  // ---- live location ---------------------------------------------------
  const [locate, setLocate] = useState('off') // off | locating | live
  const [following, setFollowing] = useState(false)
  const [headingUp, setHeadingUp] = useState(false)
  const [headingAvailable, setHeadingAvailable] = useState(false)

  const [toast, setToast] = useState(null)
  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(id)
  }, [toast])

  // ---- derived data ------------------------------------------------------
  // Status of every section at the chosen moment — computed once, shared by the
  // map and the hover tooltip.
  const statuses = useMemo(() => {
    if (!zones) return null
    const m = new Map()
    for (const f of zones.features) m.set(f.properties.id, evaluateSection(f.properties, mapDate))
    return m
  }, [zones, mapDate])

  const costs = useMemo(() => {
    if (!zones || !mapDuration) return null
    const m = new Map()
    for (const f of zones.features) m.set(f.properties.id, estimateCost(f.properties, mapDate, mapDuration))
    return m
  }, [zones, mapDate, mapDuration])

  // Adaptive colour scale: one exact colour per price for a normal stay.
  const heatScale = useMemo(() => (costs ? buildHeatScale(costs.values()) : null), [costs])

  // Picking a stay switches the map to prices; clearing it goes back to status.
  useEffect(() => {
    if (durationMin) setColorMode((m) => (m === 'status' ? 'price' : m))
    else setColorMode((m) => (m === 'price' ? 'status' : m))
  }, [durationMin])

  const centroids = useMemo(() => {
    if (!zones) return null
    const m = new Map()
    for (const f of zones.features) {
      const c = geometryCenter(f.geometry)
      if (c) m.set(f.properties.id, c)
    }
    return m
  }, [zones])

  const selectedSection = useMemo(() => {
    if (!zones || !selectedId) return null
    return zones.features.find((x) => x.properties.id === selectedId)?.properties ?? null
  }, [zones, selectedId])
  const selectedCenter = (selectedId && centroids?.get(selectedId)) || null
  const model = useMemo(
    () =>
      selectedSection
        ? sectionModel({
            section: selectedSection,
            effectiveDate,
            lang,
            t,
            estimate: costs?.get(selectedId) ?? null,
            durationMin: mapDuration,
            center: selectedCenter,
          })
        : null,
    [selectedSection, effectiveDate, lang, t, costs, selectedId, mapDuration, selectedCenter]
  )

  const holiday = useMemo(() => {
    const p = pragueParts(effectiveDate)
    return isCzechHoliday(p.year, p.month, p.day) ? czechHolidayName(p.year, p.month, p.day) : null
  }, [effectiveDate])

  // ---- actions -----------------------------------------------------------
  const sheet = mode === 'sheet'

  const changeDetent = (d) => {
    setDetent(d)
    if (view === 'home') homeDetent.current = d
  }

  const openSection = (id) => {
    if (view === 'home') homeDetent.current = detent
    setSearchActive(false)
    setLayersOpen(false)
    setSelectedId(id)
    if (sheet) setDetent('half')
    else if (collapsed) setCollapsed(false)
  }
  const closeSection = () => {
    setSelectedId(null)
    if (sheet) setDetent(homeDetent.current)
  }

  const startSearch = () => {
    if (searchActive) return
    if (view === 'home') homeDetent.current = detent
    setSelectedId(null)
    setLayersOpen(false)
    setSearchActive(true)
    setQuery((q) => q || searchPin?.label || '')
    if (sheet) setDetent('full')
    else if (collapsed) setCollapsed(false)
    // Select a prefilled place name so typing replaces it, but only in a field
    // that already has focus: select() would focus it, and the magnifier
    // deliberately doesn't.
    requestAnimationFrame(() => {
      if (document.activeElement === searchInput.current) searchInput.current.select()
    })
  }
  const endSearch = () => {
    setSearchActive(false)
    // Leaving with the box emptied means "no place": that's how the pin goes.
    if (!query.trim()) setSearchPin(null)
    searchInput.current?.blur()
    if (sheet) setDetent(homeDetent.current)
  }
  // "/" from anywhere. On a phone the field only exists once search is open,
  // so render it first, then focus it.
  const openSearch = () => {
    if (!searchInput.current) flushSync(startSearch)
    searchInput.current?.focus()
  }
  const pickResult = (r) => {
    setSearchPin({ lat: r.lat, lng: r.lng, label: r.name })
    setFlyTo({ lat: r.lat, lng: r.lng, zoom: 15.5, n: Date.now() })
    setSearchActive(false)
    setQuery('')
    searchInput.current?.blur()
    homeDetent.current = 'peek'
    if (sheet) setDetent('peek')
  }

  const stopLocate = () => {
    setLocate('off')
    setFollowing(false)
    setHeadingUp(false)
  }
  // off → locating → live (follow, north up) → heading up (if the device reports
  // a heading) → off. Panning away suspends follow; the next tap recentres.
  const onLocate = () => {
    if (locate === 'off') {
      setLocate('locating')
      setFollowing(true)
      setHeadingUp(false)
    } else if (locate === 'locating') stopLocate()
    else if (!following) setFollowing(true)
    else if (!headingUp && headingAvailable) setHeadingUp(true)
    else stopLocate()
  }
  const locateState = locate === 'live' ? (!following ? 'suspended' : headingUp ? 'heading' : 'live') : locate

  const resetNorth = () => {
    setHeadingUp(false)
    mapApi.current?.resetNorth()
  }

  // A newly opened card gets focus (screen readers announce it).
  useEffect(() => {
    if (selectedId) cardTitle.current?.focus({ preventScroll: true })
  }, [selectedId])

  // Keyboard: "/" searches, Esc steps back out of whatever is open.
  useEffect(() => {
    const onKey = (e) => {
      const typing = e.target.closest?.('input, textarea, select, [contenteditable]')
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        openSearch()
      } else if (e.key === 'Escape') {
        if (layersOpen) setLayersOpen(false)
        else if (searchActive) endSearch()
        else if (selectedId) closeSection()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  // ---- small pieces ------------------------------------------------------
  const sourceLabel = source ? t(`source${source[0].toUpperCase()}${source.slice(1)}`) : ''
  const dataAsOf = zones?.meta?.asOf
  const sourceLine = [sourceLabel && `${t('dataSource')}: ${sourceLabel}`, dataAsOf && `${t('asOf')} ${dataAsOf}`]
    .filter(Boolean)
    .join(' · ')

  const brand = !sheet && (
    <div className="brand">
      <span className="brand__logo" aria-hidden="true">
        <LogoP />
      </span>
      <span className="brand__text">
        <span className="brand__name">{t('appTitle')}</span>
        <span className="brand__tag">{t('appSubtitle')}</span>
      </span>
    </div>
  )

  const searchField = (
    <SearchField
      t={t}
      inputRef={searchInput}
      query={query}
      setQuery={setQuery}
      active={searchActive}
      onActivate={startSearch}
      onCancel={endSearch}
      // Clicking away with nothing typed goes back home: an empty results list
      // would otherwise hold the panel blank. A typed query keeps its results
      // up so one can still be picked.
      onLeave={() => !query.trim() && endSearch()}
      onSubmit={() => geo.results[0] && pickResult(geo.results[0])}
      pinLabel={searchPin?.label}
      onClearPin={() => setSearchPin(null)}
      showCancel={sheet}
      shortcutHint={finePointer && !sheet}
    />
  )

  let header
  let body
  if (view === 'section' && selectedSection && model) {
    header = <SectionHeader ref={cardTitle} t={t} section={selectedSection} model={model} onClose={closeSection} />
    body = (
      <SectionBody
        t={t}
        lang={lang}
        section={selectedSection}
        model={model}
        durationText={durationText}
        setDurationText={setDurationText}
        durationMin={durationMin}
        effectiveDate={effectiveDate}
      />
    )
  } else if (view === 'search') {
    header = (
      <div className="head">
        {brand}
        {searchField}
      </div>
    )
    body = <SearchResults t={t} geo={geo} onPick={pickResult} />
  } else {
    header = (
      <div className="head">
        {brand}
        {!sheet && searchField}
        <StayChips t={t} lang={lang} durationText={durationText} setDurationText={setDurationText} />
      </div>
    )
    body = (
      <HomeBody
        t={t}
        lang={lang}
        now={now}
        effectiveDate={effectiveDate}
        isOverride={isOverride}
        setWhen={setWhen}
        whenOpen={whenOpen}
        setWhenOpen={setWhenOpen}
        durationMin={durationMin}
        setDurationText={setDurationText}
      />
    )
  }

  // The labelled card on roomy screens; the one-line strip where map is scarce.
  const legend = zones && (
    <Legend
      key={`${colorMode}|${mode}|${shortScreen}`}
      t={t}
      theme={theme}
      colorMode={colorMode}
      durationMin={mapDuration}
      heatScale={heatScale}
      defaultOpen={!sheet && !shortScreen}
    />
  )
  const bottomControls = (
    <BottomControls
      t={t}
      finePointer={finePointer}
      onZoomIn={() => mapApi.current?.zoomIn()}
      onZoomOut={() => mapApi.current?.zoomOut()}
      locateState={locateState}
      headingAvailable={headingAvailable}
      onLocate={onLocate}
    />
  )
  const toastNode = (
    <div className="toast-region" role="status" aria-live="polite">
      {toast && <div className="toast">{toast}</div>}
    </div>
  )

  return (
    <div
      className={`app app--${mode}`}
      data-detent={sheet ? detent : undefined}
      style={{ '--occ-left': `${occlusion.left}px` }}
    >
      {!mapError && (
        <MapView
          ref={mapApi}
          zones={zones}
          statuses={statuses}
          costs={costs}
          heatScale={heatScale}
          colorMode={colorMode}
          centroids={centroids}
          selectedId={selectedId}
          onSelect={openSection}
          onMapTap={() => {
            if (layersOpen) setLayersOpen(false)
            else if (selectedId) closeSection()
          }}
          theme={theme}
          basemap={basemap}
          occlusion={occlusion}
          flyTo={flyTo}
          searchPin={searchPin}
          showHover={finePointer}
          locateOn={locate !== 'off'}
          follow={following}
          headingUp={headingUp}
          onLocated={() => setLocate('live')}
          onFollowBreak={() => setFollowing(false)}
          onHeadingBreak={() => setHeadingUp(false)}
          onHeading={() => setHeadingAvailable(true)}
          onLocateError={(code) => {
            stopLocate()
            setToast(t(LOCATE_ERRORS[code] || 'locateUnavailable'))
          }}
          onBearing={setBearing}
          onBasemapFallback={() => {
            setBasemapShown('streets')
            setToast(t('basemapFallback'))
          }}
          onError={() => setMapError(t('webglMissing'))}
          t={t}
          lang={lang}
        />
      )}

      <div className="map-ui">
        <StatusChips
          t={t}
          lang={lang}
          loading={loading}
          isOverride={isOverride}
          effectiveDate={effectiveDate}
          onResetNow={() => setWhen(null)}
          holiday={holiday}
        />
        <TopControls
          t={t}
          layersOpen={layersOpen}
          onToggleLayers={() => setLayersOpen((o) => !o)}
          // Opens search without focusing the field. Focusing while the sheet
          // was still sliding up had iOS scroll the whole page for the
          // keyboard and lose the field. A tap on the field, at the top by
          // then, raises the keyboard.
          onSearch={sheet ? startSearch : undefined}
          bearing={bearing}
          headingUp={headingUp}
          onResetNorth={resetNorth}
        />
        {layersOpen && (
          <LayersPopover
            t={t}
            theme={theme}
            basemap={basemap}
            setBasemap={setBasemap}
            colorMode={colorMode}
            setColorMode={setColorMode}
            durationMin={durationMin}
            themePref={themePref}
            setThemePref={setThemePref}
            lang={lang}
            setLang={setLang}
            sourceLine={sourceLine}
            creditsBasemap={basemapShown}
            onClose={() => setLayersOpen(false)}
          />
        )}
        {!sheet && (
          <div className="map-ui__bottom">
            <div className="map-ui__legend">{legend}</div>
            <div className="map-ui__right">{bottomControls}</div>
            {toastNode}
          </div>
        )}
      </div>

      <Panel
        mode={mode}
        detent={detent}
        onDetent={changeDetent}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        header={header}
        viewKey={`${view}|${selectedId || ''}`}
        label={t('panelLabel')}
        t={t}
        onOcclusion={setOcclusion}
        floating={
          sheet && (
            <>
              {toastNode}
              <div className="floating__row">
                <div className="floating__legend">{legend}</div>
                {bottomControls}
              </div>
            </>
          )
        }
      >
        {body}
      </Panel>

      {(error || mapError) && (
        <div className="fatal" role="alert">
          <div className="fatal__card">
            <h2 className="fatal__title">{mapError ? t('mapError') : t('errorTitle')}</h2>
            <p className="fatal__text">{mapError || error}</p>
            {!mapError && (
              <button type="button" className="btn btn--primary" onClick={() => setReloadKey((k) => k + 1)}>
                {t('retry')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
